import {
  getEffectiveProjectColor,
  hexToHsv,
  hsvToHex,
  normalizeHexInput,
  type Hsv,
} from "@/utils/projectColor";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ProjectColorPickerProps {
  /** Project id, used to derive the default color when no custom color is set. */
  projectId: string | null;
  /** The project's stored custom color (`#rrggbb`), or null for the default. */
  color: string | null | undefined;
  /**
   * Called continuously while the user is picking (dragging the square/slider or
   * typing a valid hex). Use this to update the on-screen preview only — it must
   * NOT write to the database, so a drag doesn't spam the audit log.
   */
  onPreview: (hex: string) => void;
  /**
   * Called once when the picker closes, with the final color. This is where the
   * change is persisted / synced.
   */
  onCommit: (hex: string) => void;
  disabled?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
  id?: string;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** The pure-hue background for the saturation/value square. */
function hueBackground(h: number): string {
  return (
    `linear-gradient(to top, #000, rgba(0,0,0,0)),` +
    `linear-gradient(to right, #fff, rgba(255,255,255,0)),` +
    `hsl(${h}, 100%, 50%)`
  );
}

const HUE_SLIDER_BACKGROUND =
  "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)";

/**
 * A project color picker: a small swatch button that opens a popover with a
 * saturation/value square, a hue slider, and a hex code input.
 *
 * The color updates live (`onPreview`) as the user drags or types, so project
 * color boxes recolor immediately, but the picked value is only persisted once
 * (`onCommit`) when the popover closes — so a click-and-drag no longer produces
 * a stream of database writes (CAP2-203).
 */
export function ProjectColorPicker({
  projectId,
  color,
  onPreview,
  onCommit,
  disabled,
  className,
  title = "Project color",
  ariaLabel = "Project color",
  id,
}: ProjectColorPickerProps) {
  const effective = getEffectiveProjectColor(projectId, color);

  const [open, setOpen] = useState(false);
  const [hsv, setHsvState] = useState<Hsv>({ h: 0, s: 0, v: 0 });
  const [hexText, setHexText] = useState(effective);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const squareRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const hsvRef = useRef(hsv);
  hsvRef.current = hsv;

  // Keep the latest callbacks in refs so the document-level close listener and
  // the commit path never read a stale closure.
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // The swatch shows the effective color; while closed it tracks external
  // changes (realtime sync, modal edits) since `hsv` is only meaningful open.
  const swatchColor = open ? hsvToHex(hsv.h, hsv.s, hsv.v) : effective;

  const setHsv = (next: Hsv) => {
    hsvRef.current = next;
    setHsvState(next);
    const hex = hsvToHex(next.h, next.s, next.v);
    setHexText(hex);
    onPreviewRef.current(hex);
  };

  const patchHsv = (patch: Partial<Hsv>) => {
    setHsv({ ...hsvRef.current, ...patch });
  };

  const openPicker = () => {
    if (disabled) return;
    const parsed = hexToHsv(effective) ?? { h: 0, s: 0, v: 0 };
    hsvRef.current = parsed;
    setHsvState(parsed);
    setHexText(effective);
    setOpen(true);
  };

  const closeAndCommit = () => {
    const current = hsvRef.current;
    setOpen(false);
    onCommitRef.current(hsvToHex(current.h, current.s, current.v));
  };

  const toggle = () => {
    if (open) closeAndCommit();
    else openPicker();
  };

  // Position the popover under the trigger, clamped to the viewport. Recomputed
  // on scroll/resize so it stays anchored.
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = 232;
      const gap = 6;
      let left = rect.left;
      left = Math.min(left, window.innerWidth - width - 8);
      left = Math.max(8, left);
      const estimatedHeight = 260;
      const below = rect.bottom + gap;
      const top =
        below + estimatedHeight > window.innerHeight && rect.top > estimatedHeight
          ? rect.top - gap - estimatedHeight
          : below;
      setPos({ top, left });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Close (and commit) on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      closeAndCommit();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeAndCommit();
      }
    };
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // --- Saturation/value square dragging ---
  const draggingSquare = useRef(false);
  const applySquare = (clientX: number, clientY: number) => {
    const el = squareRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const s = clamp01((clientX - rect.left) / rect.width);
    const v = 1 - clamp01((clientY - rect.top) / rect.height);
    patchHsv({ s, v });
  };
  const onSquarePointerDown = (e: React.PointerEvent) => {
    draggingSquare.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    applySquare(e.clientX, e.clientY);
  };
  const onSquarePointerMove = (e: React.PointerEvent) => {
    if (draggingSquare.current) applySquare(e.clientX, e.clientY);
  };
  const endSquareDrag = () => {
    draggingSquare.current = false;
  };
  const onSquareKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const { s, v } = hsvRef.current;
    if (e.key === "ArrowLeft") patchHsv({ s: clamp01(s - step) });
    else if (e.key === "ArrowRight") patchHsv({ s: clamp01(s + step) });
    else if (e.key === "ArrowUp") patchHsv({ v: clamp01(v + step) });
    else if (e.key === "ArrowDown") patchHsv({ v: clamp01(v - step) });
    else return;
    e.preventDefault();
  };

  // --- Hue slider dragging ---
  const draggingHue = useRef(false);
  const applyHue = (clientX: number) => {
    const el = hueRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    patchHsv({ h: clamp01((clientX - rect.left) / rect.width) * 360 });
  };
  const onHuePointerDown = (e: React.PointerEvent) => {
    draggingHue.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    applyHue(e.clientX);
  };
  const onHuePointerMove = (e: React.PointerEvent) => {
    if (draggingHue.current) applyHue(e.clientX);
  };
  const endHueDrag = () => {
    draggingHue.current = false;
  };
  const onHueKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    const { h } = hsvRef.current;
    if (e.key === "ArrowLeft") patchHsv({ h: Math.max(0, h - step) });
    else if (e.key === "ArrowRight") patchHsv({ h: Math.min(360, h + step) });
    else return;
    e.preventDefault();
  };

  // --- Hex text input ---
  const onHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setHexText(raw);
    const normalized = normalizeHexInput(raw);
    if (!normalized) return;
    const parsed = hexToHsv(normalized);
    if (!parsed) return;
    hsvRef.current = parsed;
    setHsvState(parsed);
    onPreviewRef.current(normalized);
  };
  const onHexBlur = () => {
    const normalized = normalizeHexInput(hexText);
    setHexText(normalized ?? hsvToHex(hsvRef.current.h, hsvRef.current.s, hsvRef.current.v));
  };
  const onHexKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      closeAndCommit();
    }
  };

  const squareThumb = useMemo(
    () => ({ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }),
    [hsv.s, hsv.v],
  );

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`project-color-swatch${className ? ` ${className}` : ""}`}
        style={{ backgroundColor: swatchColor }}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      />
      {open &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            className="project-color-picker"
            style={{ top: pos.top, left: pos.left }}
            role="dialog"
            aria-label={ariaLabel}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              ref={squareRef}
              className="project-color-picker__square"
              style={{ background: hueBackground(hsv.h) }}
              onPointerDown={onSquarePointerDown}
              onPointerMove={onSquarePointerMove}
              onPointerUp={endSquareDrag}
              onPointerCancel={endSquareDrag}
              onKeyDown={onSquareKeyDown}
              role="slider"
              tabIndex={0}
              aria-label="Saturation and brightness"
              aria-valuetext={`saturation ${Math.round(hsv.s * 100)}%, brightness ${Math.round(hsv.v * 100)}%`}
            >
              <span className="project-color-picker__square-thumb" style={squareThumb} />
            </div>
            <div
              ref={hueRef}
              className="project-color-picker__hue"
              style={{ background: HUE_SLIDER_BACKGROUND }}
              onPointerDown={onHuePointerDown}
              onPointerMove={onHuePointerMove}
              onPointerUp={endHueDrag}
              onPointerCancel={endHueDrag}
              onKeyDown={onHueKeyDown}
              role="slider"
              tabIndex={0}
              aria-label="Hue"
              aria-valuemin={0}
              aria-valuemax={360}
              aria-valuenow={Math.round(hsv.h)}
            >
              <span
                className="project-color-picker__hue-thumb"
                style={{ left: `${(hsv.h / 360) * 100}%` }}
              />
            </div>
            <div className="project-color-picker__hex">
              <span className="project-color-picker__hex-preview" style={{ backgroundColor: swatchColor }} />
              <input
                type="text"
                className="project-color-picker__hex-input"
                value={hexText}
                spellCheck={false}
                autoComplete="off"
                aria-label="Hex color code"
                onChange={onHexChange}
                onBlur={onHexBlur}
                onKeyDown={onHexKeyDown}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
